import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Autocomplete as MuiAutocomplete, TextField, styled, type AutocompleteRenderInputParams, type AutocompleteValue, type AutocompleteProps as MuiAutocompleteProps } from '@mui/material'
import { Chip } from '@pets-ui/Chip/Chip'
import React, { useCallback, useMemo, useState, type ReactNode, type JSX } from 'react'

const Option = styled('li')({
  fontSize: '14px',
})

const addUniqueItemsToMap = <T,>(
  array: T[],
  map: Map<string | number, T>,
  attribute: keyof T,
) => {
  array.forEach((item) => {
    const key = item[attribute]
    if (typeof key === 'string' || typeof key === 'number') {
      if (!map.has(key)) map.set(key, item)
    }
  })
}

export const mergeDedupe = <T,>(
  array1: T[],
  array2: T[],
  attribute: string,
): T[] => {
  const map = new Map<string | number, T>()
  addUniqueItemsToMap(array1, map, attribute as keyof T)
  addUniqueItemsToMap(array2, map, attribute as keyof T)
  return Array.from(map.values())
}

export interface AutocompleteOption {
  value: string
  label: string
}

export type AutocompleteProps<Value, Multiple extends boolean | undefined = true, DisableClearable extends boolean | undefined = false, FreeSolo extends boolean | undefined = false, ChipComponent extends React.ElementType = 'div'>
  /**
   * Only use defaultSelected without defaultValue to avoid confusion
   https://mui.com/material-ui/api/autocomplete/?srsltid=AfmBOooX6NWh4UE2e9uO8icsBAWWiBhedmR2x6QH6tNxrp7eBM7giuMQ
   */
  = Omit<MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>, 'renderInput' | 'defaultValue'> & {
    setSelection?: (s: string) => void
    defaultSelected?: string
    placeholder: string
    renderInput?: (params: unknown) => JSX.Element
    error?: boolean
    helperText?: ReactNode
    inputRef?: React.Ref<HTMLInputElement>
    maxHeight?: number
  }
// 若發生在onInputChange中自動reset user input的狀況，須考慮將freesolo設為true ref. https://github.com/mui/material-ui/issues/32425
export const Autocomplete = <Multiple extends boolean | undefined = true, DisableClearable extends boolean | undefined = false, FreeSolo extends boolean | undefined = false, ChipComponent extends React.ElementType = 'div'>(
  props: AutocompleteProps<AutocompleteOption, Multiple, DisableClearable, FreeSolo, ChipComponent>,
) => {
  const {
    setSelection,
    defaultSelected = '',
    options,
    placeholder,
    multiple = true,
    fullWidth = true,
    renderInput,
    error,
    helperText,
    inputRef,
    maxHeight,
    ...rest
  } = props
  const [selectedOptions, setSelectedOptions] = useState<AutocompleteValue<AutocompleteOption, Multiple, DisableClearable, FreeSolo>[]>([])

  const defaultSelectedOptions: AutocompleteValue<AutocompleteOption, Multiple, DisableClearable, FreeSolo>[] = useMemo(() => {
    const selected = defaultSelected.split(',')
    // FIXME: Selected should be able to process FreeSolo type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (options as any).filter((option: { value: string }) => selected.includes(option.value))
  }, [defaultSelected, options])

  const handleChange = useCallback((
    _event: React.SyntheticEvent<Element, Event>,
    value: AutocompleteValue<AutocompleteOption, Multiple, DisableClearable, FreeSolo>,
  ) => {
    const newValue = Array.isArray(value) ? value : value ? [value] : []
    setSelectedOptions(newValue)
    const selectedIds = newValue.map(option => option.value).join(',')
    setSelection?.(selectedIds)
  }, [setSelection])

  const renderInputDefault = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      inputRef={inputRef}
      sx={{
        '& .MuiOutlinedInput-root': {
          'fontSize': '14px',
          'minHeight': '36px',
          // Input field's background color
          'backgroundColor': 'white',
          '& .MuiAutocomplete-input': {
            padding: '0 0 0 8px !important',
          },
          // Apply red border when helperText or error is present
          '& fieldset': {
            borderColor: error || helperText ? '#FF5353 !important' : '',
          },
        },
        '& .MuiFormHelperText-root > p': {
          color: '#FF5353 !important',
          margin: '4px 0 0 0 !important',
          // Keep helper text background transparent
          backgroundColor: 'transparent !important',
        },
        '& .Mui-disabled': {
          transition: '0.2s ease-in-out',
          backgroundColor: '#EEEEEE',
        },
        '& .MuiFormHelperText-root': {
          color: '#FF5353 !important',
          margin: '4px 0 0 0 !important',
        },
      }}
      {...params}
      error={Boolean(error || helperText)}
      helperText={helperText}
      placeholder={(Array.isArray(value) && value.length > 0) ? '' : placeholder}
    />
  )

  const value = useMemo(() => multiple && Array.isArray(selectedOptions)
    ? mergeDedupe(defaultSelectedOptions, selectedOptions, 'value')
    // Default value MUST not be undefined
    : (selectedOptions[0] ?? defaultSelectedOptions[0]) || '',
  [defaultSelectedOptions, multiple, selectedOptions])

  return (
    <MuiAutocomplete
      // FIXME: Should not use as Multiple
      multiple={multiple as Multiple}
      fullWidth={fullWidth}
      options={options}
      getOptionLabel={option => typeof option === 'string' ? option : option.label}
      onChange={handleChange}
      // FIXME: Should not use as AutocompleteValue
      value={value as AutocompleteValue<AutocompleteOption, Multiple, DisableClearable, FreeSolo>}
      renderTags={(value, getTagProps) => (
        <div
          style={{
            maxHeight: maxHeight ? `${maxHeight}px` : 'none',
            overflowY: 'auto',
            display: 'flex',
            flexWrap: 'wrap',
          }}
        >
          {value.map((option, index) => {
            const tagProps = getTagProps({ index })
            const { onDelete, disabled } = tagProps

            return (
              <Chip
                key={option.value}
                onDelete={disabled ? undefined : onDelete}
                margin={1}
                size="small"
              >
                {option.label}
              </Chip>
            )
          })}
        </div>
      )}
      popupIcon={<KeyboardArrowDownIcon />}
      renderOption={(props, option) => (
        <Option {...props} key={option.value}>{option.label}</Option>
      )}
      renderInput={renderInput ?? renderInputDefault}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      {...rest}
    />
  )
}
